import { openingForFinding, type GuidedStep, type OpenRow } from './guidedSteps.ts'
import {
  activesBoard,
  checkSides,
  columnStatusFor,
  type ActiveRow,
  type ColumnStatus,
} from './verificationItems.ts'
import { CHECK_LABEL, label } from './vocabulary.ts'

/**
 * The adversarial review, as the screens need it (#541).
 *
 * The enums mirror `@rp/core`'s `adversarial-review.ts` by hand: the web app is
 * its own build and cannot import the server's packages. `adversarialReview.test.ts`
 * reads that file and fails when the two disagree.
 */

export enum ReviewVerdict {
  ReadingWrong = 'reading_wrong',
  PairingWrong = 'pairing_wrong',
  FalseFinding = 'false_finding',
  MissedFinding = 'missed_finding',
}

export enum ReviewLayer {
  Reader = 'reader',
  Pairing = 'pairing',
  Check = 'check',
  ErpData = 'erp_data',
}

export enum ReviewRunStatus {
  Running = 'running',
  Done = 'done',
  Failed = 'failed',
}

export enum ReviewAssessment {
  Right = 'right',
  Wrong = 'wrong',
}

/** In the order the result is read: what we raised wrongly, then what we missed. */
export const VERDICT_ORDER: readonly ReviewVerdict[] = [
  ReviewVerdict.FalseFinding,
  ReviewVerdict.MissedFinding,
  ReviewVerdict.PairingWrong,
  ReviewVerdict.ReadingWrong,
]

export const VERDICT_LABEL: Readonly<Record<ReviewVerdict, string>> = {
  [ReviewVerdict.FalseFinding]: 'Apontamento indevido',
  [ReviewVerdict.MissedFinding]: 'Divergência não apontada',
  [ReviewVerdict.PairingWrong]: 'Pareamento errado',
  [ReviewVerdict.ReadingWrong]: 'Leitura errada',
}

export const ASSESSMENT_LABEL: Readonly<Record<ReviewAssessment, string>> = {
  [ReviewAssessment.Right]: 'Acertou',
  [ReviewAssessment.Wrong]: 'Errou',
}

export const LAYER_LABEL: Readonly<Record<ReviewLayer, string>> = {
  [ReviewLayer.Reader]: 'Leitura',
  [ReviewLayer.Pairing]: 'Pareamento',
  [ReviewLayer.Check]: 'Regra de comparação',
  [ReviewLayer.ErpData]: 'Cadastro do ERP',
}

export interface ReviewDisagreement {
  id: string
  verdict: ReviewVerdict
  layer: ReviewLayer
  anchor: { finding?: string | undefined; preparation?: string | undefined; blocks: string[] }
  reason: string
  pipelineRead?: string | undefined
  reviewRead?: string | undefined
}

export interface ReviewAssessmentRecord {
  assessment: ReviewAssessment
  by: string
  at: string
}

/** One run, as `GET /api/adversarial-reviews/:verificationId` answers it. */
export interface AdversarialRun {
  id: string
  status: ReviewRunStatus
  requestedBy: string
  model: string | null
  /** The conferência was read or scored again after this run. */
  stale: boolean
  /** The receita has been read again; what is left of a running run is the critique. */
  blindRead: boolean
  disagreements: ReviewDisagreement[]
  /** Disagreements dropped for pointing at something not on the conferência. */
  rejected: number
  assessments: Record<string, ReviewAssessmentRecord>
  error: string | null
  createdAt: string
  finishedAt: string | null
}

/**
 * What the control offers, which is a question about the latest run.
 *
 * `Outdated` is its own answer rather than a flag on `Ready`: the disagreements
 * of an outdated run point at findings that have since been rewritten, so they
 * are not opened at all -- the only thing to do with one is run again.
 */
export enum ReviewOffer {
  /** Nothing has been run. */
  None = 'none',
  Running = 'running',
  Failed = 'failed',
  Outdated = 'outdated',
  Ready = 'ready',
}

export function reviewOffer(run: AdversarialRun | null | undefined): ReviewOffer {
  if (run === null || run === undefined) return ReviewOffer.None
  if (run.status === ReviewRunStatus.Running) return ReviewOffer.Running
  if (run.status === ReviewRunStatus.Failed) return ReviewOffer.Failed
  return run.stale ? ReviewOffer.Outdated : ReviewOffer.Ready
}

/**
 * The step a disagreement belongs on, or undefined when it has none.
 *
 * By the finding it names, and failing that by the keyed preparation. One that
 * names neither -- a preparation the reader never saw -- has no step to stand
 * on and is shown only in the result.
 */
export function stepIndexOf(
  steps: readonly GuidedStep[],
  disagreement: ReviewDisagreement,
): number | undefined {
  const { finding, preparation } = disagreement.anchor
  if (finding !== undefined) {
    const index = steps.findIndex((step) => step.findings.some((f) => f.fingerprint === finding))
    if (index !== -1) return index
  }
  if (preparation !== undefined) {
    const index = steps.findIndex(
      (step) => step.kind === 'item' && step.item?.preparationId === preparation,
    )
    if (index !== -1) return index
  }
  return undefined
}

export interface DisputedStep {
  /** Its place among all the steps, which is what a jump is given. */
  readonly index: number
  readonly step: GuidedStep
  readonly disagreements: readonly ReviewDisagreement[]
}

/** The steps the review disputes, in the order the conferência walks them. */
export function disputedSteps(
  steps: readonly GuidedStep[],
  disagreements: readonly ReviewDisagreement[],
): readonly DisputedStep[] {
  const byStep = new Map<number, ReviewDisagreement[]>()
  for (const one of disagreements) {
    const index = stepIndexOf(steps, one)
    if (index === undefined) continue
    byStep.set(index, [...(byStep.get(index) ?? []), one])
  }
  return [...byStep.entries()]
    .sort(([a], [b]) => a - b)
    .map(([index, own]) => ({ index, step: steps[index]!, disagreements: own }))
}

/**
 * The rows a disputed step opens pointing at: what the review disputes.
 *
 * A disagreement about a finding pins that finding's own row, as a link to
 * the finding would. One with no finding -- a missed divergence -- has no row,
 * so the blocks the review cited are pinned under a key of their own: the
 * receita still frames what it is talking about.
 */
export function reviewPins(disputed: DisputedStep): readonly OpenRow[] {
  const pins = new Map<string, OpenRow>()
  for (const one of disputed.disagreements) {
    for (const pin of disagreementPins(disputed.step, one)) pins.set(pin.key, pin)
  }
  return [...pins.values()]
}

/**
 * The same, for one disagreement: what the tour points at on its stop.
 *
 * One row, wherever one can be told. A finding is often drawn across several
 * rows -- composition is the whole list of ativos -- and pointing at the first
 * of them put the balloon on PENTRAVAN for a dispute about ETOXIDIGLICOL. So
 * among the rows a dispute could be about, the one it is about is chosen by
 * what the review itself said: the blocks it cited, then the names it quoted.
 */
export function disagreementPins(
  step: GuidedStep,
  disagreement: ReviewDisagreement,
): readonly OpenRow[] {
  const { finding, blocks } = disagreement.anchor

  const row = rowInDispute(step, disagreement)
  if (row !== undefined) return [row]

  const opening = finding === undefined ? undefined : openingForFinding([step], finding)
  if (opening !== undefined && opening.pins.length > 0) return opening.pins

  // A check with a board of its own, named by no finding: the block it cites.
  const cited = new Set(blocks)
  const board = step.findings.find(
    (one) => one.check !== 'composition' && one.regions.some((ref) => cited.has(ref)),
  )
  if (board !== undefined) {
    return [{ key: board.fingerprint, regions: board.regions, tone: 'brand' }]
  }

  if (blocks.length === 0) return []
  return [{ key: `review:${disagreement.id}`, regions: blocks, tone: 'brand' }]
}

/** How much each kind of evidence says a row is the one in dispute. */
const CITED_BLOCK = 4
/*
 * Above a cited block. The readings are the two versions of the one thing in
 * dispute, while the blocks are whatever on the page shows it -- and for a
 * line the reader invented, that is the lines that are really there.
 */
const QUOTED_IN_A_READING = 5
const NAMED_IN_THE_REASON = 1
/** As little as a passing mention: on its own it only breaks a tie towards the finding's rows. */
const DRAWN_FROM_THE_FINDING = 1

/** A name too short to mean anything when found inside a sentence. */
const SHORTEST_NAME = 4

/**
 * The ativo row a disagreement is about, or undefined when none stands out.
 *
 * Among all of the entry's rows, not only the ones the named finding is drawn
 * on: a composition finding sits on the unpaired lines, and a dispute about it
 * is as often about a line that was paired. A row scores for citing a block
 * the review cited, for being named in either reading it quoted, and -- least,
 * because a reason mentions its neighbours too -- for being named in the reason
 * or for carrying the finding.
 */
function rowInDispute(step: GuidedStep, disagreement: ReviewDisagreement): OpenRow | undefined {
  const row = activeRowInDispute(step, disagreement)
  if (row === undefined) return undefined
  return {
    key: row.key,
    /*
     * A row with no place on the receita -- a line the reader invented -- is
     * framed by what the review cited instead: the lines that are really
     * there, which is the evidence that this one is not.
     */
    regions: row.regions.length > 0 ? row.regions : disagreement.anchor.blocks,
    tone: row.status === 'error' ? 'error' : 'brand',
  }
}

/** The same row, whole: the result draws its two sides. */
function activeRowInDispute(
  step: GuidedStep,
  disagreement: ReviewDisagreement,
): ActiveRow | undefined {
  if (step.item === undefined) return undefined
  const { finding, blocks } = disagreement.anchor
  const rows = activesBoard(step.item).rows

  const cited = new Set(blocks)
  const readings = plain(`${disagreement.pipelineRead ?? ''} ${disagreement.reviewRead ?? ''}`)
  const reason = plain(disagreement.reason)

  let best: { row: (typeof rows)[number]; score: number } | undefined
  for (const row of rows) {
    const names = [row.name, row.quotedName]
      .filter((name): name is string => name !== undefined)
      .map(plain)
      .filter((name) => name.length >= SHORTEST_NAME)
    const score =
      (row.findings.some((one) => one.fingerprint === finding) ? DRAWN_FROM_THE_FINDING : 0) +
      (row.regions.some((ref) => cited.has(ref)) ? CITED_BLOCK : 0) +
      (names.some((name) => readings.includes(name)) ? QUOTED_IN_A_READING : 0) +
      (names.some((name) => reason.includes(name)) ? NAMED_IN_THE_REASON : 0)
    // The first of equals: rows are in the order the entry lists them.
    if (score > 0 && (best === undefined || score > best.score)) best = { row, score }
  }
  return best?.row
}

/** Upper case, no accents: how two spellings of one name are compared. */
function plain(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
}

/** One stop of the guided tour: a disagreement, and the disputed step it is on. */
export interface TourStop {
  /** The step's place among the disputed steps, which is what the walk is in. */
  readonly stepAt: number
  readonly step: GuidedStep
  readonly disagreement: ReviewDisagreement
}

/**
 * The guided tour: every disagreement that has a step, one stop each, in the
 * order the conferência walks them.
 *
 * By disagreement rather than by step, because that is the unit somebody
 * answers "was the review right?" about. Two disputes on one preparation are
 * two stops on the same screen.
 */
export function reviewTour(disputed: readonly DisputedStep[]): readonly TourStop[] {
  return disputed.flatMap((one, stepAt) =>
    one.disagreements.map((disagreement) => ({ stepAt, step: one.step, disagreement })),
  )
}

/** Where the tour is for a step: its first stop there, or the start. */
export function firstStopOn(tour: readonly TourStop[], stepAt: number): number {
  const at = tour.findIndex((stop) => stop.stepAt === stepAt)
  return at === -1 ? 0 : at
}

/** The disagreements with no step: about the conferência as a whole. */
export function unplacedDisagreements(
  steps: readonly GuidedStep[],
  disagreements: readonly ReviewDisagreement[],
): readonly ReviewDisagreement[] {
  return disagreements.filter((one) => stepIndexOf(steps, one) === undefined)
}

/**
 * One row of the result: a disagreement, and the line of the conferência it is
 * about -- drawn the way the conferência draws it, receita beside sistema.
 */
export interface ResultRow {
  readonly disagreement: ReviewDisagreement
  /** Its step among all the steps; undefined when it has none. */
  readonly stepIndex: number | undefined
  /** The entry: a step's title, or the conferência as a whole. */
  readonly where: string
  /** The section of that entry the line is in: Ativos, Posologia. */
  readonly what: string
  /** The line's two sides, as the conferência shows them. Empty when it has none. */
  readonly prescribed: string
  readonly quoted: string
  /** How the conferência coloured the line, so the result colours it the same. */
  readonly status: ColumnStatus
}

/** What a disagreement about no step is filed under. */
export const WHOLE_VERIFICATION = 'Conferência inteira'

/** The section the ativos are drawn under on the conferência. */
const ACTIVES_SECTION = 'Ativos'

/** A line that could not be told: the entry is known, its section is not. */
export const ABSENT_LINE = '—'
const ABSENT = ABSENT_LINE

/**
 * The result's rows: what has no step first, then the rest in the order the
 * conferência walks them -- the order `disputedSteps` and the tour use, so the
 * table, the tour and the page agree about what comes next.
 */
export function resultRows(
  steps: readonly GuidedStep[],
  disagreements: readonly ReviewDisagreement[],
): readonly ResultRow[] {
  const placed = disputedSteps(steps, disagreements).flatMap(
    ({ index, step, disagreements: own }) =>
      own.map((disagreement) => ({
        disagreement,
        stepIndex: index,
        where: step.badge === undefined ? step.title : `${step.badge} · ${step.title}`,
        ...lineOf(step, disagreement),
      })),
  )
  const unplaced = unplacedDisagreements(steps, disagreements).map((disagreement) => ({
    disagreement,
    stepIndex: undefined,
    where: WHOLE_VERIFICATION,
    what: ABSENT,
    prescribed: '',
    quoted: '',
    status: 'no_issues' as const,
  }))
  return [...unplaced, ...placed]
}

/**
 * The one line of an entry a disagreement is about.
 *
 * A check with a board of its own -- form, posologia, volume -- is that
 * board's line. Anything about the ingredients is the ativo row in dispute,
 * which is the same row the tour points at: composition has no line of its
 * own, it is the list.
 */
function lineOf(
  step: GuidedStep,
  disagreement: ReviewDisagreement,
): Pick<ResultRow, 'what' | 'prescribed' | 'quoted' | 'status'> {
  const finding = step.findings.find((one) => one.fingerprint === disagreement.anchor.finding)
  const aboutIngredients =
    finding === undefined || finding.scope === 'ingredient' || finding.check === 'composition'

  const active = aboutIngredients ? activeRowInDispute(step, disagreement) : undefined
  if (active !== undefined) {
    return {
      what: ACTIVES_SECTION,
      prescribed: [active.name, active.prescribedDose].filter(Boolean).join('  '),
      quoted: [active.quotedName, active.quotedDose].filter(Boolean).join('  '),
      status: active.status,
    }
  }
  if (finding !== undefined) {
    const sides = checkSides(finding)
    return {
      what: label(CHECK_LABEL, finding.check),
      prescribed: sides.prescribed ?? '',
      quoted: sides.quoted ?? '',
      status: columnStatusFor(finding.disposition),
    }
  }
  return { what: ABSENT, prescribed: '', quoted: '', status: 'no_issues' }
}

/** The findings a set of disagreements names, for a card that shows only those. */
export function disputedFingerprints(
  disagreements: readonly ReviewDisagreement[],
): ReadonlySet<string> {
  return new Set(
    disagreements.flatMap((one) => (one.anchor.finding === undefined ? [] : [one.anchor.finding])),
  )
}

/** "3 discordâncias", "1 discordância", "nenhuma discordância". */
export function disagreementCount(count: number): string {
  if (count === 0) return 'nenhuma discordância'
  return count === 1 ? '1 discordância' : `${String(count)} discordâncias`
}
